import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { num } from "@/lib/format";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Check, Layers2 } from "lucide-react";

const parse = (v) => Number(String(v).trim().replace(",", "."));

const InlineNumber = ({ initial, label, onSave, onCancel, testid }) => {
  const [v, setV] = useState(String(initial ?? ""));
  const ok = () => { const n = parse(v); if (Number.isFinite(n) && n >= 0) onSave(n); else onCancel(); };
  return (
    <div className="mt-1 flex items-center justify-center gap-1">
      <Input autoFocus value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") ok(); if (e.key === "Escape") onCancel(); }}
        className="h-7 w-20 text-center text-xs" aria-label={label} data-testid={testid} />
      <Button size="icon" className="h-7 w-7" onClick={ok} data-testid={`${testid}-ok`}><Check className="h-3.5 w-3.5" /></Button>
    </div>
  );
};

// Metri da adesivare / adesivati di una variante di tessuto
export const FuseControl = ({ m }) => {
  const qc = useQueryClient();
  const [mode, setMode] = useState(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["materials"] });
  const err = (e) => toast.error(e.response?.data?.detail || "Operazione non riuscita");
  const setToFuse = useMutation({ mutationFn: async (n) => (await api.patch(`/materials/${m.id}`, { to_fuse: n })).data, onSuccess: () => { refresh(); setMode(null); }, onError: err });
  const fuse = useMutation({ mutationFn: async (n) => (await api.post(`/materials/${m.id}/fuse`, { delta: n })).data, onSuccess: (_d, n) => { refresh(); setMode(null); toast.success(`${num(n)} m segnati come adesivati`); }, onError: err });
  const toFuse = m.to_fuse || 0;
  const fused = m.fused || 0;
  return (
    <div className="mt-1 space-y-0.5 text-[11px]" data-testid={`mat-fuse-${m.id}`}>
      {toFuse > 0 && (
        <p className="flex items-center justify-center gap-1 text-amber-400">
          <button type="button" className="underline decoration-dotted underline-offset-2" onClick={() => setMode("set")} data-testid={`mat-to-fuse-${m.id}`}>{num(toFuse)} m da adesivare</button>
          <button type="button" className="rounded border border-amber-500/50 px-1 hover:bg-amber-500/10" onClick={() => setMode("fuse")} data-testid={`mat-fuse-btn-${m.id}`}>Adesivati</button>
        </p>
      )}
      {fused > 0 && <p className="text-sky-400" data-testid={`mat-fused-${m.id}`}>{num(fused)} m adesivati</p>}
      {!toFuse && !mode && (
        <button type="button" className="flex w-full items-center justify-center gap-1 text-muted-foreground hover:text-foreground" onClick={() => setMode("set")} data-testid={`mat-set-to-fuse-${m.id}`}>
          <Layers2 className="h-3 w-3" />Da adesivare
        </button>
      )}
      {mode === "set" && <InlineNumber initial={toFuse || ""} label="Metri da adesivare" onSave={(n) => setToFuse.mutate(n)} onCancel={() => setMode(null)} testid={`mat-to-fuse-input-${m.id}`} />}
      {mode === "fuse" && (
        <>
          <p className="text-center text-muted-foreground">Quanti metri sono stati adesivati?</p>
          <InlineNumber initial={toFuse} label="Metri adesivati" onSave={(n) => fuse.mutate(n)} onCancel={() => setMode(null)} testid={`mat-fuse-input-${m.id}`} />
        </>
      )}
    </div>
  );
};
